/**
 * HOTFIX purchase-indexed-grid-refresh — pure wiring helpers.
 *
 * After a real purchase reaches `indexed_successfully` (backend-authoritative
 * indexing confirmation, see purchase-indexing.ts), the shared Grid must
 * refresh its parcel tiles automatically so the newly indexed parcel becomes
 * visible WITHOUT a manual browser refresh.
 *
 * These are the two pure decisions behind that wiring, extracted so they are
 * deterministically unit-testable and can never drift into a false refresh:
 *   - `shouldTriggerGridRefresh`: refresh fires ONLY on real indexing success
 *     (never on confirmed_onchain / pending / delayed / anomaly / idle).
 *   - `refreshSignalChanged`: an edge detector so a monotonically-bumped
 *     signal nonce triggers exactly one refresh per change and NEVER on the
 *     initial mount value (no repeated / infinite refresh loops).
 *
 * No pricing / SaleV2 / approve / purchase / quote / indexer-authority / chain
 * logic lives here — this is purely UI state synchronization.
 */
import type { IndexingStateKind } from "./purchase-indexing";

/** Refresh the Grid ONLY when the backend has authoritatively indexed the
 *  purchase. `confirmed_onchain` alone is NOT enough (§ the parcel record is
 *  not canonical yet); every other state is a no-op. */
export function shouldTriggerGridRefresh(kind: IndexingStateKind): boolean {
  return kind === "indexed_successfully";
}

/** Edge detector for a monotonic refresh-signal nonce.
 *  Returns true iff `next` is a real, defined value that differs from a real,
 *  previously-seen value — so the initial mount value (prev === undefined)
 *  never triggers a refresh and an unchanged value is idempotent. */
export function refreshSignalChanged(
  prev: number | undefined,
  next: number | undefined,
): boolean {
  if (next === undefined) return false;
  if (prev === undefined) return false;
  return next !== prev;
}
